import { ThemeProvider } from "@mui/material";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import secureLocalStorage from "react-secure-storage";
import { darkTheme, lightTheme } from "../theme";

export type ThemeMode = "light" | "dark" | "system";
const THEME_MODE_KEY = "themeMode";

const ThemeModeContext = createContext({
    mode: "light" as ThemeMode,
    setMode: (_mode: ThemeMode) => {}
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const ThemeModeProvider = ({ children }: { children: React.ReactNode }) => {

    const getInitialMode = (): ThemeMode => {
        const storedMode = secureLocalStorage.getItem(THEME_MODE_KEY) as ThemeMode | null;
        return storedMode || "light";
    };

    const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

    const setMode = (newMode: ThemeMode) => {
        secureLocalStorage.setItem(THEME_MODE_KEY, newMode);
        setModeState(newMode);
    };

    useEffect(() => {
        const storedMode = secureLocalStorage.getItem(THEME_MODE_KEY) as ThemeMode | null;
        if (!storedMode) {
            secureLocalStorage.setItem(THEME_MODE_KEY, "light");
        }
    }, []);

    const theme = useMemo(() => {
        const effectiveMode = mode === "system" ? "light" : mode;
        return effectiveMode === "dark" ? darkTheme : lightTheme;
    }, [mode]);

    return (
        <ThemeModeContext.Provider value={{ mode, setMode }}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeModeContext.Provider>
    );
};
